import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store';
import LoadingSpinner from '../ui/LoadingSpinner';
import { buildFullUrl, storeRedirectUrl } from '../../utils/redirectUrl';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredSubRole?: string;
  allowedRoles?: ('super_admin' | 'admin' | 'tenant')[];
  allowedCompanyRoles?: ('owner' | 'manager' | 'accountant' | 'designer' | 'maintenance_coordinator' | 'field_staff' | 'sales_agent' | 'customer_support')[];
  requiredFeatures?: string[];
  requireAllFeatures?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredSubRole,
  allowedRoles,
  allowedCompanyRoles,
  requiredFeatures,
  requireAllFeatures = true
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, token, currentCompanyUser } = useAppSelector(state => state.auth);

  // Show loading if we're fetching data OR if we have auth state but no user yet
  if (isLoading || (isAuthenticated && token && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const redirectUrl = buildFullUrl(location);
    storeRedirectUrl(redirectUrl);
    return <Navigate to="/auth/login" state={{ from: redirectUrl }} replace />;
  }

  // Check role-based access
  if (allowedRoles || allowedCompanyRoles) {
    // Check user role (super_admin, admin, tenant)
    const userRole = user.role;
    // Check company role (owner, manager, etc.)
    const companyRole = currentCompanyUser?.role;

    let hasUserRoleAccess = false;
    let hasCompanyRoleAccess = false;

    if (allowedRoles) {
      hasUserRoleAccess = allowedRoles.includes(userRole as any);
    }

    if (allowedCompanyRoles && companyRole) {
      hasCompanyRoleAccess = allowedCompanyRoles.includes(companyRole as any);
    }

    const hasAccess = hasUserRoleAccess || hasCompanyRoleAccess;

    if (!hasAccess) {
      // Redirect based on user role
      if (user.role === 'tenant') {
        return <Navigate to="/tenant" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (requiredRole && user.role !== requiredRole && user.role !== 'super_admin') {
    // Redirect based on user role
    if (user.role === 'tenant') {
      return <Navigate to="/tenant" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredSubRole && user.role === 'admin' && user.subRole !== requiredSubRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;